import {
  adminTournamentsApi,
  ADMIN_TOURNAMENT_SPORTS,
  type AdminTournament,
  type AdminTournamentSport,
} from '@/api/admin/adminTournamentsApi';
import { adminImpersonationApi } from '@/api/admin/adminImpersonationApi';
import { AdminPager } from '@/components/admin/AdminPager';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { usePageResetOnFilterChange } from '@/hooks/usePageResetOnFilterChange';
import { useAuthStore } from '@/store/authStore';
import { handleGlobalError } from '@/utils/error-handler';
import {
  getStatusColor,
  getStatusLabel,
  TournamentStatus,
} from '@/utils/tournament-status';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const PAGE_SIZE = 20;

const SPORT_LABELS: Record<AdminTournamentSport, string> = {
  PADEL: 'Pádel',
  TENNIS: 'Tenis',
  SQUASH: 'Squash',
  FOOTBALL: 'Fútbol',
};

// Falls back to the raw uid: the backend can seed a sport the enum here has not
// caught up with, and an empty cell would read as "no sport".
const getSportLabel = (sport: string) =>
  SPORT_LABELS[sport as AdminTournamentSport] ?? sport;

const AdminTorneosPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const startImpersonation = useAuthStore((state) => state.startImpersonation);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<TournamentStatus | ''>('');
  const [sport, setSport] = useState<AdminTournamentSport | ''>('');
  const [page, setPage] = useState(0);
  const [tournaments, setTournaments] = useState<AdminTournament[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [enteringId, setEnteringId] = useState<string | null>(null);
  // The guard has to be a ref: two clicks inside one tick both read `enteringId`
  // as null, because React has not re-rendered between them. The state drives
  // the button label; this is what actually stops the second impersonation.
  const isEnteringRef = useRef(false);

  // The search runs against every tournament on the platform: debounced so a
  // typed term costs one query instead of one per keystroke.
  const debouncedQuery = useDebouncedValue(query);
  const requestId = useRef(0);

  const load = useCallback(async () => {
    // Two fetches can still be in flight at once (a new term resets the page,
    // and both changes fire a load). Only the newest response may write to
    // state, or a slow earlier one repaints the table with stale results.
    const current = ++requestId.current;
    setIsLoading(true);
    try {
      const result = await adminTournamentsApi.list({
        query: debouncedQuery,
        status,
        sport,
        page,
        size: PAGE_SIZE,
      });
      if (current !== requestId.current) return;
      setTournaments(result.content);
      setTotalPages(result.totalPages);
    } catch (error) {
      if (current !== requestId.current) return;
      handleGlobalError(
        error,
        'No se pudieron cargar los torneos',
        'Revisá la conexión e intentá de nuevo.',
      );
    } finally {
      if (current === requestId.current) {
        setIsLoading(false);
      }
    }
  }, [debouncedQuery, status, sport, page]);

  useEffect(() => {
    load();
  }, [load]);

  usePageResetOnFilterChange(
    `${debouncedQuery}|${status}|${sport}`,
    page,
    setPage,
  );

  const enter = async (tournament: AdminTournament) => {
    // Guarded across the whole table, not per row: a second click while the
    // first entry is still resolving fires a second impersonation the store
    // then refuses, leaving an audit row with no session behind it — and now
    // also a "ya hay una sesión activa" toast for something the admin only
    // asked for once.
    if (isEnteringRef.current) return;
    isEnteringRef.current = true;
    setEnteringId(tournament.id);
    try {
      const result = await adminImpersonationApi.impersonate(
        tournament.ownerPk,
        tournament.id,
      );
      const started = startImpersonation(result);
      // Only navigate when the session was installed. A refusal means some other
      // session is already active, and entering the tournament with it would
      // reach the backend as an owner who did not create this tournament.
      //
      // Said out loud, like UserActionsMenu does: returning in silence leaves
      // the button going from "Entrando..." back to "Entrar" with nothing to
      // read, and no way to guess that the blocker is a support session open
      // somewhere else.
      if (!started) {
        toast({
          variant: 'destructive',
          title: 'No se pudo abrir la sesión de soporte',
          description:
            'Ya hay una sesión de soporte activa. Salí de ella desde el cartel inferior y volvé a intentarlo.',
        });
        return;
      }
      navigate(`/torneos/${tournament.id}`);
    } catch (error) {
      handleGlobalError(
        error,
        'No se pudo abrir la sesión de soporte',
        'Intentá de nuevo en un momento.',
      );
    } finally {
      isEnteringRef.current = false;
      setEnteringId(null);
    }
  };

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Torneos</h1>
        <p className="text-sm text-gray-500">
          Todos los torneos de la plataforma. "Entrar" abre una sesión de soporte
          en modo lectura sobre el dueño del torneo.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Buscar por torneo, club o dueño"
          className="max-w-md"
        />
        <select
          value={status}
          onChange={(event) =>
            setStatus(event.target.value as TournamentStatus | '')
          }
          className="h-9 rounded-md border border-gray-200 bg-white px-3 text-sm"
        >
          <option value="">Todos los estados</option>
          {Object.values(TournamentStatus).map((value) => (
            <option key={value} value={value}>
              {getStatusLabel(value)}
            </option>
          ))}
        </select>
        <select
          value={sport}
          onChange={(event) =>
            setSport(event.target.value as AdminTournamentSport | '')
          }
          className="h-9 rounded-md border border-gray-200 bg-white px-3 text-sm"
        >
          <option value="">Todos los deportes</option>
          {ADMIN_TOURNAMENT_SPORTS.map((value) => (
            <option key={value} value={value}>
              {SPORT_LABELS[value]}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-12" />
          ))}
        </div>
      ) : tournaments.length === 0 ? (
        <p className="py-10 text-center text-sm text-gray-500">
          No hay torneos que coincidan.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Torneo</TableHead>
              <TableHead>Club</TableHead>
              <TableHead>Deporte</TableHead>
              <TableHead>Dueño</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead>Fecha</TableHead>
              <TableHead className="w-28" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {tournaments.map((tournament) => (
              <TableRow key={tournament.id}>
                <TableCell className="font-medium">{tournament.name}</TableCell>
                <TableCell className="text-gray-600">
                  {tournament.clubNames.join(', ') || '—'}
                </TableCell>
                <TableCell className="text-gray-600">
                  {getSportLabel(tournament.sport)}
                </TableCell>
                <TableCell className="text-gray-600">
                  {tournament.ownerName || tournament.ownerEmail}
                </TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={getStatusColor(tournament.status)}
                  >
                    {getStatusLabel(tournament.status)}
                  </Badge>
                </TableCell>
                <TableCell className="text-gray-600">
                  {new Date(tournament.startDate).toLocaleDateString('es-AR')}
                </TableCell>
                <TableCell>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={enteringId !== null}
                    onClick={() => enter(tournament)}
                  >
                    {enteringId === tournament.id ? 'Entrando...' : 'Entrar'}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <AdminPager
        page={page}
        totalPages={totalPages}
        isLoading={isLoading}
        onPageChange={setPage}
      />
    </div>
  );
};

export default AdminTorneosPage;
